/**
 * Talking to `staff_console`.
 *
 * One endpoint, one action name per call. The publishable key goes on
 * `apikey` through `supabaseFetch`, which is also what refuses to put a key on
 * `Authorization`; the console's session goes on `Authorization: Bearer` (a
 * Supabase Auth access token, which is a JWT) or on `x-plt-console-session`
 * (the seeded session). DECISIONS_LOG.md, 4 October, item 7.
 *
 * The browser never sends a database function name. The action name is looked
 * up in the function's own allow-list, and `p_email` is taken from the session
 * there, so nothing a page sends can act for somebody else.
 */

import { supabaseFetch } from '../lib/api.ts'
import type { PublicConfig } from '../lib/env.ts'
import { sessionHeaders, type StoredSession } from './session.ts'

export class ConsoleError extends Error {
  readonly status: number
  readonly code: string
  readonly hint: string | null
  /**
   * What the endpoint sent with the refusal, where it sent anything. The
   * staff editor refuses a commit whose warnings are not all ticked
   * (§11.11.5) and returns the evaluation with the refusal, so the screen can
   * show the ticks that are missing rather than only the sentence.
   */
  readonly result: unknown
  /**
   * The whole refusal body, not just its `result`.
   *
   * `result` is the RPC's own return value and is nested; fields the endpoint
   * adds *beside* it are not in there. `order_moved` is one — `staff_console`
   * puts it at the top level, next to `result` — and reading it off `result`
   * finds nothing at all, which is how the first version of this fix came to
   * pass its own tests while changing nothing on screen.
   */
  readonly body: unknown
  constructor(
    status: number,
    code: string,
    message: string,
    hint: string | null = null,
    result: unknown = null,
    body: unknown = null,
  ) {
    super(message)
    this.name = 'ConsoleError'
    this.status = status
    this.code = code
    this.hint = hint
    this.result = result
    this.body = body
  }
}

/** A refusal with no sentence of its own still has to read as Hungarian on a
 *  Hungarian screen (§5.12). */
function wordIt(status: number): string {
  if (status === 401) return 'A munkamenet lejárt. Jelentkezzen be újra.'
  if (status === 403) return 'Ehhez a nézethez nincs jogosultsága.'
  if (status === 404) return 'Nincs ilyen tétel.'
  if (status === 409) return 'A művelet most nem végezhető el.'
  return 'A konzol most nem érhető el. Kérjük, próbálja meg újra.'
}

export interface CallOptions {
  config: PublicConfig
  session: StoredSession | null
  /** A Google access token, when the call is the sign-in itself and there is
   *  no stored session yet. */
  accessToken?: string
  fetchImpl?: typeof fetch
}

export async function consoleCall<T>(
  action: string,
  args: Record<string, unknown>,
  options: CallOptions,
): Promise<T> {
  return call('staff_console', action, args, options)
}

/**
 * The staff editor of §11.11 is a second endpoint, for the reason its own
 * file gives: it re-runs the engine between the request and the write, which
 * an action-to-RPC allow-list cannot. Same keys, same session headers, same
 * refusals — so the only difference worth having is the path.
 */
export async function editorCall<T>(
  action: string,
  args: Record<string, unknown>,
  options: CallOptions,
): Promise<T> {
  return call('staff_editor', action, args, options)
}

/**
 * A2a and A6 of the catalogue brief of 19 October 2026: the console's publish
 * button calls `catalogue-sync` directly, and not an action on
 * `staff_console`.
 *
 * That is the point of the button. Every trigger — the hourly job, the Sheet
 * menu, `pnpm catalogue:sync` and this — goes through the one shared module,
 * so they take the same lock, apply the same gate, write the same run row and
 * give the same answer. An action on `staff_console` would have been a second
 * path to the same work, which is how two of them come to disagree.
 *
 * The function has `verify_jwt = false` and does its own authentication: it
 * reads the staff session off these very headers and asks
 * `public.staff_may_sync_catalogue`. A refusal is a 403 with
 * `status: "forbidden"`, and 409 with `status: "running"` is another sync
 * already in flight — both are answers, not errors, so this returns the body
 * rather than throwing on them.
 */
export interface CatalogueSyncAnswer {
  status: 'published' | 'unchanged' | 'refused' | 'running' | 'forbidden'
  published: number
  /** Aktív = TRUE and stopped by the gate: the dishes somebody has to fix. */
  withheld: Array<{ id: string; name_hu: string; reason: string }>
  /** How many rows are Aktív = FALSE — parked, not broken. A count only. */
  inactive: number
  reason?: string
  run_id?: number | null
  sheet_modified_time?: string | null
  drive_problem?: string | null
}

export async function catalogueSyncNow(options: CallOptions): Promise<CatalogueSyncAnswer> {
  const { config, session, fetchImpl } = options
  const bearer = session?.provider === 'google' ? session.accessToken : undefined

  let response: Response
  try {
    response = await supabaseFetch(
      config.supabaseUrl,
      '/functions/v1/catalogue-sync',
      {
        method: 'POST',
        apiKey: config.supabasePublishableKey,
        ...(bearer === undefined ? {} : { accessToken: bearer }),
        headers: { 'content-type': 'application/json', ...sessionHeaders(session) },
        body: JSON.stringify({ source: 'console' }),
      },
      fetchImpl,
    )
  } catch {
    throw new ConsoleError(0, 'unreachable', 'A közzététel most nem érhető el.')
  }

  let body: Record<string, unknown> = {}
  try {
    body = (await response.json()) as Record<string, unknown>
  } catch {
    body = {}
  }
  const status = typeof body.status === 'string' ? body.status : ''
  if (status === '') {
    throw new ConsoleError(
      response.status,
      typeof body.code === 'string' ? body.code : 'error',
      typeof body.message === 'string' && body.message !== '' ? body.message : wordIt(response.status),
    )
  }
  return {
    status: status as CatalogueSyncAnswer['status'],
    published: typeof body.published === 'number' ? body.published : 0,
    withheld: Array.isArray(body.withheld) ? (body.withheld as CatalogueSyncAnswer['withheld']) : [],
    inactive: typeof body.inactive === 'number' ? body.inactive : 0,
    reason: typeof body.reason === 'string' ? body.reason : undefined,
    run_id: typeof body.run_id === 'number' ? body.run_id : null,
    sheet_modified_time: typeof body.sheet_modified_time === 'string' ? body.sheet_modified_time : null,
    drive_problem: typeof body.drive_problem === 'string' ? body.drive_problem : null,
  }
}

async function call<T>(
  fn: 'staff_console' | 'staff_editor',
  action: string,
  args: Record<string, unknown>,
  options: CallOptions,
): Promise<T> {
  const { config, session, accessToken, fetchImpl } = options
  const bearer = accessToken ?? (session?.provider === 'google' ? session.accessToken : undefined)

  let response: Response
  try {
    response = await supabaseFetch(
      config.supabaseUrl,
      `/functions/v1/${fn}`,
      {
        method: 'POST',
        apiKey: config.supabasePublishableKey,
        ...(bearer === undefined ? {} : { accessToken: bearer }),
        headers: { 'content-type': 'application/json', ...sessionHeaders(session) },
        body: JSON.stringify({ action, ...args }),
      },
      fetchImpl,
    )
  } catch {
    throw new ConsoleError(0, 'unreachable', 'A konzol most nem érhető el.')
  }

  let body: Record<string, unknown> = {}
  try {
    body = (await response.json()) as Record<string, unknown>
  } catch {
    body = {}
  }

  if (!response.ok || body.ok !== true) {
    const message = typeof body.message === 'string' && body.message !== ''
      ? body.message
      : wordIt(response.status)
    throw new ConsoleError(
      response.status,
      typeof body.code === 'string' ? body.code : 'error',
      message,
      typeof body.hint === 'string' ? body.hint : null,
      body.result ?? null,
      body,
    )
  }

  return (body.result ?? body) as T
}
